import { useState } from 'react';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { PurchaseOrder } from 'src/models/Management.interface';
import { apiUrls, errorMessages } from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { ca } from 'date-fns/locale';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { status } from 'nprogress';

interface PaginatedPurchaseOrderResponse {
  data: PurchaseOrder[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

export const useGetPurchaseOrder = () => {
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { showSnackbar } = StyledSnackbar();

  const fetchPurchaseOrders = async (
    page = 1,
    limit = 100,
    search = '',
    sortBy = 'vendorName',
    order = 'ASC',
    filter = {},
    status?: string,
    vendorId?: string,
    startDate?: Date | null,
    endDate?: Date | null
  ) => {
    setLoading(true);
    try {
      const response =
        await axiosTokenInstance.get<PaginatedPurchaseOrderResponse>(
          apiUrls.PURCHASE_ORDER_MANAGEMENT,
          {
            params: {
              offset: page,
              limit: limit,
              search: search,
              sortBy: sortBy,
              order: order,
              ...filter,
            },
            withCredentials: true,
          }
        );

      setPurchaseOrders(response.data.data || []);
      setTotalItems(response.data.totalItems || 0);
      setTotalPages(response.data.totalPages || 0);
      setCurrentPage(response.data.currentPage || 1);
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.error(error.response.data.message);
        setPurchaseOrders([]);
      } else {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  const deletePurchaseOrder = async (id: string) => {
    try {
      const response = await axiosTokenInstance.delete(
        apiUrls.DELETE_PURCHASE_ORDERS,
        {
          params: { id },
        }
      );
      showSnackbar(response.data.message, 'success');
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  return {
    loading,
    purchaseOrders,
    totalItems,
    totalPages,
    currentPage,
    fetchPurchaseOrders,
    deletePurchaseOrder,
  };
};
